import {
  createFileRoute,
  redirect,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { getDefaultStore, useAtomValue } from "jotai";
import { type JSX, useState } from "react";
import { z } from "zod";

import { RecurrenceCreateHeader } from "@/components/recurring-appointment/recurrence-create-header";
import { Button } from "@/components/ui/button/button";
import { Page } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import {
  CardSelectItem,
  CardSelectItemContainer,
  CardSelectRoot,
  CardSelectTitle,
} from "@/components/ui/select-input/card-select";
import { batchAdjustTargetsAtom } from "@/lib/atoms/batch-adjust-form-atom";
import { BATCH_ADJUST_MINUTE_OPTIONS, directionLabel } from "@/lib/helpers/batch-adjust";

const searchSchema = z.object({
  direction: z.enum(["advance", "delay"]),
});

const targetsCountLabel = (count: number): string =>
  count === 1 ? "1 agendamento" : `${count} agendamentos`;

const BatchAdjustMinutesPage = (): JSX.Element => {
  const navigate = useNavigate();
  const { direction } = useSearch({ from: "/calendar/batch-adjust/minutes" });
  const targets = useAtomValue(batchAdjustTargetsAtom);
  const [minutes, setMinutes] = useState<number | null>(null);

  const label = directionLabel(direction);

  const handleContinue = (): void => {
    if (minutes === null) return;
    void navigate({
      to: "/calendar/batch-adjust/review",
      search: { direction, minutes },
    });
  };

  return (
    <RoundedLayout className="-mb-[env(safe-area-inset-bottom)]">
      <RecurrenceCreateHeader
        title={`${label} horário`}
        subtitle={targetsCountLabel(targets.length)}
      />
      <Page className="flex flex-col">
        <div className="flex h-full flex-col justify-between gap-8">
          <div className="flex flex-col gap-8">
            <CardSelectRoot
              name="batch-adjust-minutes"
              defaultValue={minutes !== null ? String(minutes) : undefined}
              onChange={(value) => setMinutes(Number(value))}
              className="gap-4"
            >
              <CardSelectTitle>
                Em quantos minutos deseja {label.toLowerCase()} os agendamentos?
              </CardSelectTitle>
              <CardSelectItemContainer>
                {BATCH_ADJUST_MINUTE_OPTIONS.map((option) => (
                  <CardSelectItem key={option} value={String(option)}>
                    {direction === "advance" ? "−" : "+"}
                    {option} min
                  </CardSelectItem>
                ))}
              </CardSelectItemContainer>
            </CardSelectRoot>
          </div>

          <div className="sticky bottom-0 bg-white pt-2 pb-2">
            <Button fullWidth disabled={minutes === null} onClick={handleContinue}>
              Continuar
            </Button>
          </div>
        </div>
      </Page>
    </RoundedLayout>
  );
};

export const Route = createFileRoute("/calendar/batch-adjust/minutes")({
  component: BatchAdjustMinutesPage,
  validateSearch: searchSchema,
  beforeLoad: ({ context }) => {
    if (!context.flags["ajuste-em-lote-de-agendamentos"]) {
      redirect({ to: "/calendar", throw: true });
    }
    if (getDefaultStore().get(batchAdjustTargetsAtom).length === 0) {
      redirect({ to: "/calendar", throw: true });
    }
  },
});
